import { z } from "zod"

import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"
import { platformSupportScopeV1Schema } from "./platform-workforce-v1.js"
import { tenantIdSchema } from "./identifiers.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{2,95}$/)

export const supportSessionModeV1Schema = z.enum(["standard", "break_glass"])
export const supportSessionStateV1Schema = z.enum(["active", "revoked", "expired", "closed"])
export const supportAccessActivityV1Schema = z.enum([
  "view", "query", "action", "denial", "export",
])

export const supportSessionAuditReceiptV1Schema = z.object({
  supportSessionEventId: z.uuid(),
  supportSessionId: z.uuid(),
  supportAccessGrantId: z.uuid(),
  targetTenantId: tenantIdSchema,
  platformOperatorId: z.uuid(),
  platformSupportCaseId: z.uuid(),
  activity: supportAccessActivityV1Schema,
  resourceTypeCode: codeSchema,
  outcomeCode: codeSchema,
  correlationId: z.uuid(),
  occurredAt: timestampSchema,
  tenantAuditEventId: z.uuid(),
  platformAuditEventId: z.uuid(),
  export: z.boolean(),
  safeMetadataOnly: z.literal(true),
}).strict().superRefine((event, context) => {
  if (event.export !== (event.activity === "export")) {
    context.addIssue({ code: "custom", path: ["export"], message: "Export evidence must agree with activity." })
  }
})

export const auditedSupportSessionV1Schema = z.object({
  contractVersion: z.literal(1),
  supportSessionId: z.uuid(),
  supportAccessGrantId: z.uuid(),
  targetTenantId: tenantIdSchema,
  platformOperatorId: z.uuid(),
  platformSupportCaseId: z.uuid(),
  scopes: z.array(platformSupportScopeV1Schema).min(1).max(5),
  mode: supportSessionModeV1Schema,
  state: supportSessionStateV1Schema,
  startedAt: timestampSchema,
  expiresAt: timestampSchema,
  revokedAt: timestampSchema.nullable(),
  revocationReasonCode: codeSchema.nullable(),
  breakGlassReviewDueAt: timestampSchema.nullable(),
  breakGlassReviewState: z.enum(["not_required", "pending", "completed"]),
  breakGlassReviewedByPlatformOperatorId: z.uuid().nullable(),
  cachePartitionKey: z.string().regex(/^support:[0-9a-f-]{36}:[0-9a-f-]{36}$/),
  revision: z.number().int().positive().safe(),
  updatedAt: timestampSchema,
  tenantDataCached: z.literal(false),
}).strict().superRefine((session, context) => {
  const duration = Date.parse(session.expiresAt) - Date.parse(session.startedAt)
  if (duration <= 0 || (session.mode === "break_glass" && duration > 15 * 60 * 1_000)) {
    context.addIssue({ code: "custom", path: ["expiresAt"], message: "Break-glass duration is capped at 15 minutes." })
  }
  if ((session.state === "revoked") !== (session.revokedAt !== null && session.revocationReasonCode !== null)) {
    context.addIssue({ code: "custom", path: ["revokedAt"], message: "Revocation evidence must agree with session state." })
  }
  if (session.mode === "break_glass") {
    if (session.breakGlassReviewDueAt === null || session.breakGlassReviewState === "not_required") {
      context.addIssue({ code: "custom", path: ["breakGlassReviewState"], message: "Break-glass requires automatic review." })
    }
  } else if (session.breakGlassReviewDueAt !== null || session.breakGlassReviewState !== "not_required") {
    context.addIssue({ code: "custom", path: ["breakGlassReviewState"], message: "Standard sessions cannot create break-glass review." })
  }
})

const openActionSchema = z.object({
  action: z.literal("open"),
  supportSessionId: z.uuid(),
  supportAccessGrantId: z.uuid(),
  targetTenantId: tenantIdSchema,
  platformSupportCaseId: z.uuid(),
  mode: supportSessionModeV1Schema,
}).strict()
const recordActionSchema = z.object({
  action: z.literal("record_access"),
  supportSessionId: z.uuid(),
  targetTenantId: tenantIdSchema,
  activity: supportAccessActivityV1Schema,
  resourceTypeCode: codeSchema,
  outcomeCode: codeSchema,
}).strict()
const revokeActionSchema = z.object({
  action: z.literal("revoke"),
  supportSessionId: z.uuid(),
  targetTenantId: tenantIdSchema,
  reasonCode: codeSchema,
}).strict()
const simpleAction = <T extends "expire" | "close" | "review_break_glass">(
  action: T,
) => z.object({
  action: z.literal(action),
  supportSessionId: z.uuid(),
  targetTenantId: tenantIdSchema,
}).strict()

export const manageAuditedSupportSessionActionV1Schema = z.discriminatedUnion("action", [
  openActionSchema,
  recordActionSchema,
  revokeActionSchema,
  simpleAction("expire"),
  simpleAction("close"),
  simpleAction("review_break_glass"),
])

export const manageAuditedSupportSessionV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema.safeExtend({
    commandType: z.literal("ManageAuditedSupportSession"),
  }),
  request: manageAuditedSupportSessionActionV1Schema,
}).strict().superRefine((command, context) => {
  const requiresApproval = command.request.action === "open"
    && command.request.mode === "break_glass"
  if (
    command.envelope.idempotencyNamespace !== "support.session.manage"
    || command.envelope.target.scope !== "tenant"
    || command.envelope.target.targetTenantId !== command.request.targetTenantId
    || command.envelope.target.targetType !== "support_session"
    || command.envelope.target.targetId !== command.request.supportSessionId
    || (requiresApproval && command.envelope.requiredApprovalIds.length !== 1)
    || (!requiresApproval && command.envelope.requiredApprovalIds.length !== 0)
  ) {
    context.addIssue({ code: "custom", path: ["envelope"], message: "Support session command must bind one exact Tenant/session and break-glass approval." })
  }
})

export type AuditedSupportSessionV1 = z.infer<typeof auditedSupportSessionV1Schema>
export type ManageAuditedSupportSessionV1 = z.infer<typeof manageAuditedSupportSessionV1Schema>
export type SupportSessionAuditReceiptV1 = z.infer<typeof supportSessionAuditReceiptV1Schema>
